/**
 * Hook pour gérer les statistiques de stream
 * @module pages/hooks/useStreamStats
 */

import { useState, useEffect, useCallback } from 'react';
import StreamStatsService from '../../services/StreamStatsService';
import ChannelEventsService from '../../services/ChannelEventsService';
import { buildDailyEvolution } from '../utils/streamStatsEvolution';

const STREAM_EVENT_TYPES = ['follow', 'unfollow', 'sub', 'resub', 'giftsub'];
const EVENTS_PAGE_SIZE = 100;
const MAX_EVENT_PAGES = 20;

/**
 * Récupère les événements nécessaires à la construction des graphiques d'évolution.
 * @returns {Promise<Array>}
 */
async function fetchEvolutionEvents() {
  const events = [];
  let offset = 0;

  const minimumDate = new Date();
  minimumDate.setHours(0, 0, 0, 0);
  minimumDate.setDate(minimumDate.getDate() - 29);

  for (let page = 0; page < MAX_EVENT_PAGES; page += 1) {
    const response = await ChannelEventsService.getChannelEvents({
      limit: EVENTS_PAGE_SIZE,
      offset,
      type: STREAM_EVENT_TYPES,
    });
    const pageData = Array.isArray(response?.data) ? response.data : [];
    if (pageData.length === 0) {
      break;
    }

    events.push(...pageData);

    const oldestEvent = pageData[pageData.length - 1];
    const oldestEventDate = oldestEvent?.timestamp
      ? new Date(oldestEvent.timestamp)
      : null;
    if (
      !oldestEventDate ||
      Number.isNaN(oldestEventDate.getTime()) ||
      oldestEventDate < minimumDate
    ) {
      break;
    }

    if (pageData.length < EVENTS_PAGE_SIZE) {
      break;
    }

    offset += EVENTS_PAGE_SIZE;
  }

  return events;
}

/**
 * Hook de gestion des statistiques de stream
 * @param {string} range - Période ('7' ou '30')
 * @returns {Object} État et données des statistiques
 */
export const useStreamStats = (range = '7') => {
  const [stats, setStats] = useState(null);
  const [evolution, setEvolution] = useState({
    last7Days: [],
    last30Days: [],
  });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  /**
   * Récupère les statistiques
   */
  const fetchStats = useCallback(async () => {
    try {
      setError(null);
      setLoading(true);
      const [streamStatsResponse, events] = await Promise.all([
        StreamStatsService.getStreamStats(),
        fetchEvolutionEvents(),
      ]);
      const now = new Date();
      setEvolution({
        last7Days: buildDailyEvolution(events, 7, now),
        last30Days: buildDailyEvolution(events, 30, now),
      });

      const response = streamStatsResponse;
      setStats(response.data);
    } catch (err) {
      console.error('Erreur lors du chargement des stats de stream:', err);
      setError(
        err.message || 'Erreur lors du chargement des statistiques de stream'
      );
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchStats();
  }, [fetchStats]);

  const currentEvents =
    range === '7' ? stats?.last7Days?.events : stats?.last30Days?.events;

  const titleRange = range === '7' ? '7 derniers jours' : '30 derniers jours';

  return {
    stats,
    evolution,
    loading,
    error,
    currentEvents,
    titleRange,
    refetch: fetchStats,
  };
};
